import type { FC, FormEvent, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { api, errorMessage } from '@/lib/client/api'
import { Button } from '@/components/ui/button'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { AuthShell } from '@/components/auth/auth-shell'
import { FormError, FormField } from '@/components/auth/form-field'
import { safeNext } from '@/lib/navigation'
import { withGuest } from '@/lib/server/auth'

const Signup: FC = (): ReactNode => {
    const router = useRouter()
    const next = safeNext(router.query.next, '/create')
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)

    const submit = async (event: FormEvent): Promise<void> => {
        event.preventDefault()

        if (password.length < 8) {
            setError('Use at least 8 characters for your password.')

            return
        }

        setBusy(true)
        setError(null)

        try {
            await api('/api/auth/signup', { method: 'POST', body: { name, email, password } })
            await router.replace(next)
        }
        catch (caught) {
            setError(errorMessage(caught))
            setBusy(false)
        }
    }

    const loginHref = next === '/create' ? '/login' : `/login?next=${encodeURIComponent(next)}`

    return (
        <Layout variant='bare'>
            <Seo title='Sign up' description='Create a Feedsmith account and turn your blog posts into Instagram carousels.' />

            <AuthShell
                title='Create your account'
                subtitle='Your posts, feeds and brand kit are saved to it and follow you to any device.'
                footer={(
                    <div className='flex flex-col gap-3'>
                        <p>
                            Already have an account?{' '}
                            <Link href={loginHref} className='font-medium text-foreground underline underline-offset-4'>
                                Log in
                            </Link>
                        </p>
                        <p className='text-xs'>
                            By signing up you agree to how we handle your data, described in our{' '}
                            <Link href='/privacy' className='underline underline-offset-4'>
                                privacy notice
                            </Link>
                            .
                        </p>
                    </div>
                )}
            >
                <form onSubmit={submit} className='flex flex-col gap-5' noValidate>
                    <FormField id='name' label='Your name' autoComplete='name' required value={name} onChange={event => setName(event.target.value)} />
                    <FormField id='email' label='Email' type='email' autoComplete='email' required value={email} onChange={event => setEmail(event.target.value)} />
                    <FormField
                        id='password'
                        label='Password'
                        type='password'
                        autoComplete='new-password'
                        required
                        minLength={8}
                        value={password}
                        onChange={event => setPassword(event.target.value)}
                        hint={<span className='text-xs text-muted-foreground'>At least 8 characters</span>}
                    />

                    <FormError message={error} />

                    <Button type='submit' disabled={busy} className='h-11 rounded-full text-base'>
                        {busy && <Loader2 className='size-4 animate-spin' />}
                        Create account
                    </Button>
                </form>
            </AuthShell>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps = async ctx => withGuest(ctx, '/create')

export default Signup
